import { Formik } from "formik";
import Loader from "../../loader";
import StepsDots from "../../form/StepsDots";
import ErrorMessage from "../../form/ErrorMessage";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { profileStep4Schema } from "../../../utils/schema";
import { findAJobPaths } from "../../../routes/find-a-job";
import {
  setIsFindAJobModal,
  setRegisterStep,
} from "../../../redux/features/find-a-job/reducers/authReducer";
import {
  fetchfreelancerProfile,
  profileStep4Submit,
} from "../../../redux/features/find-a-job/actions/authAction";
import {
  clearFreelancerFormData,
  getFreelancerFormData,
  getProfile,
  getToken,
  setProfile,
  updateFreelancerFormData,
} from "../../../utils/pip";
import { useEffect, useState } from "react";

const ProfileSetupStep4 = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { subLoading } = useSelector((state) => state.freelancer.auth);
  const [savedData, setSavedData] = useState({});

  const parseArray = (value, fallback = []) => {
    if (Array.isArray(value)) return value;
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (!trimmed) return fallback;
      try {
        const parsed = JSON.parse(trimmed);
        return Array.isArray(parsed) ? parsed : fallback;
      } catch {
        return trimmed
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
      }
    }
    return fallback;
  };
  const handleStep4 = (values) => {
    const { consent_accuracy, consent_contact, ...rest } = values;
    updateFreelancerFormData("profileStep4", values);

    const allData = values || {};
    const step1 = allData.step1 || {};
    const step1Profile = allData.profileStep1 || {};
    const step2Profile = allData.profileStep2 || {};
    const step3Profile = allData.profileStep3 || {};
    const step4Profile = { ...rest };

    const full_name = (step1.full_name || "").trim();
    const first_name = full_name;
    const last_name = "";

    const open_talent = {
      phone_number: step1Profile.phone_number || "",
      country: step1Profile.country || "",
      city: step1Profile.city || "",
      linkedin_profile: step1Profile.linkedin_profile || "",
      portfolio_website: step1Profile.portfolio_website || "",
      bio: step1Profile.bio || "",
      location:
        step1Profile.country && step1Profile.city
          ? `${step1Profile.city}, ${step1Profile.country}`
          : step1Profile.country || step1Profile.city || "",
      primary_role: step2Profile.primary_role || "",
      talent_role: step2Profile.primary_role || "",
      years_of_experience: step2Profile.years_of_experience || "",
      key_skills: (step2Profile.key_skills || []).join(","),
      tools: (step2Profile.tools || []).join(","),
      availability: step2Profile.availability || "",
      hourly_rate: step2Profile.hourly_rate || "",
      preferred_project_type: (step2Profile.preferred_project_type || []).join(","),
      experiences: step3Profile.experiences || [],
      portfolio_links: step3Profile.portfolio_links || [],
      work_samples: step3Profile.work_samples || [],
      join_reason: step4Profile.join_reason || "",
      interested_clients: step4Profile.interested_clients || "",
      heard_about: step4Profile.heard_about || "",
      talent_type: step1.talent_type || "FREELANCER",
    };

    const payload = {
      why_join_nuvio: step4Profile.join_reason || "",
      how_hear_about_nuvio: step4Profile.heard_about || "",
      clients_types: step4Profile.interested_clients || "",
      information_confirmed: !!values?.consent_accuracy,
      agree_contact_opportunities: !!values?.consent_contact,
    };

    const callback = (response) => {
      if (!response?.success) return;
      const existingProfile = getProfile("freelancer") || {};
      const isOpenTalent =
        existingProfile?.role === "OPEN_TALENT" ||
        !!existingProfile?.open_talent ||
        existingProfile?.open_talent?.talent_type === "FREELANCER";
      const token = getToken("freelancer");

      setProfile("freelancer", {
        first_name,
        last_name,
        email: step1.email || "",
        open_talent,
      });
      clearFreelancerFormData();
      localStorage.setItem("freelancer_free_trial_modal", "1");
      if (!token || !isOpenTalent) {
        dispatch(setIsFindAJobModal(false));
        dispatch(setRegisterStep(1));
        navigate(findAJobPaths.Dashboard);
        return;
      }
      dispatch(setRegisterStep("subscription"));
    };

    dispatch(profileStep4Submit({ payload, callback }));
  };

  useEffect(() => {
    dispatch(fetchfreelancerProfile()).then((res) => {
      const data = res?.payload?.data || {};
      const openTalent = data?.open_talent || {};
      setSavedData({
        join_reason: openTalent?.why_join_nuvio || "",
        interested_clients: openTalent?.clients_types || "",
        heard_about: openTalent?.how_hear_about_nuvio || "",
        consent_accuracy: !!openTalent?.information_confirmed,
        consent_contact: !!openTalent?.agree_contact_opportunities,
        step1: {
          full_name: data?.full_name || "",
          email: data?.email || "",
          talent_type: openTalent?.talent_type || "FREELANCER",
        },
        profileStep1: {
          phone_number: data?.phone_number || "",
          country: openTalent?.country || "",
          city: openTalent?.city || "",
          linkedin_profile: openTalent?.linkedin_profile || "",
          portfolio_website: openTalent?.website_or_portfolio || "",
          bio: openTalent?.bio || "",
        },
        profileStep2: {
          primary_role: openTalent?.primary_role || "",
          years_of_experience: openTalent?.years_of_experience || "",
          key_skills: openTalent?.skills ? openTalent.skills.split(",") : [],
          tools: openTalent?.tools_software_used
            ? openTalent.tools_software_used.split(",")
            : [],
          availability: openTalent?.availability || "",
          hourly_rate: openTalent?.hourly_rate || "",
          preferred_project_type: openTalent?.preferred_project_type
            ? openTalent.preferred_project_type.split(",")
            : [],
        },
        profileStep3: {
          experiences: data?.experiences || [],
          portfolio_links: openTalent?.sample_portfolio_link
            ? [openTalent.sample_portfolio_link]
            : [""],
          work_samples: openTalent?.projects || [],
          cv: openTalent?.cv || data?.cv || [],
          certificates: openTalent?.certificates || data?.certificates || [],
        },
      });
    });
  }, [dispatch]);

  const refreshStep3FromProfile = async () => {
    const localStep3 = getFreelancerFormData()?.profileStep3 || {};
    const res = await dispatch(fetchfreelancerProfile());
    const data = res?.payload?.data;
    if (!data) return;
    const openTalent = data.open_talent || {};
    updateFreelancerFormData("profileStep3", {
      experiences:
        localStep3?.experiences && localStep3.experiences.length
          ? localStep3.experiences
          : parseArray(data.experiences || [], []),
      portfolio_links:
        localStep3?.portfolio_links && localStep3.portfolio_links.length
          ? localStep3.portfolio_links
          : openTalent.sample_portfolio_link
            ? [openTalent.sample_portfolio_link]
            : [""],
      work_samples: parseArray(openTalent.projects || [], []),
      cv: parseArray(openTalent.cv || data.cv || [], []),
      certificates: parseArray(openTalent.certificates || data.certificates || [], []),
      delete_project_ids: [],
      delete_certificate_ids: [],
    });
  };

  const handlePrevious = async () => {
    await refreshStep3FromProfile();
    dispatch(setRegisterStep("profileStep3"));
  };

  if (subLoading) {
    return <Loader />;
  }
  return (
    <div className="modal-body pe-0">
      <section className="ct_signup_main py-3">
        <div className="container-fluid">
          <div className="row align-items-center">
            <div className="col-xxl-4 col-xl-4 col-lg-4 mb-4 mb-lg-0 d-none d-lg-block">
              <div className="ct_signup_left_img">
                <img
                  src="../assets/img/setp_6_freelance_img_left.png"
                  alt=""
                  style={{ objectFit: "cover" }}
                />
              </div>
            </div>
            <div className="col-xxl-8 col-xl-8 col-lg-8 mb-4 mb-lg-0 ">
              <div className="ct_signup_right_content px-5 ct_custom_scroll">
                <div className="ct_signup_logo ct_mb_34">
                  <img src="../assets/img/Nuvio_logo.png" alt="" />
                </div>
                <div className="mt-0 text-center">
                  <h2 className="ct_fs_32  mb-0 ct_darkblue_text">
                    NUVIO Network
                  </h2>
                  <p className="mb-0 ct_fs_16 mt-2 ct_darkblue_text mx-auto">
                    Help us understand your goals and how we can connect you with
                    the right clients.
                  </p>
                </div>
                <Formik
                  initialValues={{
                    join_reason: savedData?.join_reason || "",
                    interested_clients: savedData?.interested_clients || "",
                    heard_about: savedData?.heard_about || "",
                    consent_accuracy: savedData?.consent_accuracy || false,
                    consent_contact: savedData?.consent_contact || false,
                    step1: savedData?.step1,
                    profileStep1: savedData?.profileStep1,
                    profileStep2: savedData?.profileStep2,
                    profileStep3: savedData?.profileStep3,
                  }}
                  validationSchema={profileStep4Schema}
                  validateOnChange={true}
                  validateOnBlur={true}
                  enableReinitialize
                  onSubmit={(values, actions) => {
                    handleStep4(values);
                  }}
                >
                  {({
                    values,
                    errors,
                    touched,
                    handleChange,
                    handleBlur,
                    handleSubmit,
                    setFieldValue,
                    setTouched,
                  }) => (
                    <form onSubmit={handleSubmit} className="ct_mt_40">
                      <div className="row">
                        <div className="col-md-12">
                          <div className="form-group mb-4">
                            <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                              Why do you want to join NUVIO?
                            </label>
                            <textarea
                              className="form-control ct_input h-auto"
                              rows={4}
                              placeholder="Share your motivation"
                              id="join_reason"
                              onBlur={handleBlur}
                              onChange={handleChange}
                              value={values?.join_reason}
                            />
                            <ErrorMessage
                              errors={errors}
                              touched={touched}
                              fieldName={"join_reason"}
                            />
                          </div>
                        </div>
                        <div className="col-md-12">
                          <div className="form-group mb-4">
                            <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                              What type of clients are you interested in working with?
                            </label>
                            <textarea
                              className="form-control ct_input h-auto"
                              rows={4}
                              placeholder="Describe the clients or industries you prefer"
                              id="interested_clients"
                              onBlur={handleBlur}
                              onChange={handleChange}
                              value={values?.interested_clients}
                            />
                            <ErrorMessage
                              errors={errors}
                              touched={touched}
                              fieldName={"interested_clients"}
                            />
                          </div>
                        </div>
                        <div className="col-md-12">
                          <div className="form-group mb-4">
                            <label className="mb-2 d-block ct_fs_14 ct_darkblue_text">
                              How did you hear about NUVIO?
                            </label>
                            <div className="d-flex flex-column gap-2 position-relative">
                              <select
                                className="form-control ct_input"
                                id="heard_about"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                value={values?.heard_about}
                              >
                                <option value="">Select an option</option>
                                <option value="LinkedIn">LinkedIn</option>
                                <option value="Referral">Referral</option>
                                <option value="Website">Website</option>
                                <option value="Event / Webinar">Event / Webinar</option>
                                <option value="Other">Other</option>
                              </select>
                              <i className="fa-solid fa-angle-down ct_show_eye ct_pointer_events_none"></i>
                            </div>
                            <ErrorMessage
                              errors={errors}
                              touched={touched}
                              fieldName={"heard_about"}
                            />
                          </div>
                        </div>
                        <div className="col-md-12">
                          <div className="form-group mb-3">
                            <label className="ct_checkbox_label d-flex gap-2">
                              <input
                                type="checkbox"
                                id="consent_accuracy"
                                checked={values.consent_accuracy}
                                onChange={handleChange}
                              />
                              <span className="ct_fs_14 ct_darkblue_text">
                                I confirm that the information provided is accurate.
                              </span>
                            </label>
                            <ErrorMessage
                              errors={errors}
                              touched={touched}
                              fieldName="consent_accuracy"
                            />
                          </div>
                        </div>
                        <div className="col-md-12">
                          <div className="form-group mb-3">
                            <label className="ct_checkbox_label d-flex gap-2">
                              <input
                                type="checkbox"
                                id="consent_contact"
                                checked={values.consent_contact}
                                onChange={handleChange}
                              />
                              <span className="ct_fs_14 ct_darkblue_text">
                                I agree to be contacted by NUVIO regarding freelance opportunities.
                              </span>
                            </label>
                            <ErrorMessage
                              errors={errors}
                              touched={touched}
                              fieldName="consent_contact"
                            />
                          </div>
                        </div>
                      </div>
                      <div className="d-flex gap-2 mt-2">
                        <button
                          type="button"
                          className="ct_outline_btn w-100 mx-auto"
                          onClick={handlePrevious}
                        >
                          Previous
                        </button>
                        <button
                          type="submit"
                          onClick={handleSubmit}
                          className="ct_yellow_btn  w-100 mx-auto"
                        >
                          Continue
                        </button>
                      </div>
                      <StepsDots steps={[1, 2, 3, 4]} activeStep={4} />
                    </form>
                  )}
                </Formik>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProfileSetupStep4;
